// Sugalarm — provider-specific credential form. Both adapters take an
// email/username + password; Dexcom needs a region; Libre detects its
// region on redirect server-side.

import { useState } from "react";

import * as cgmApi from "../../api/cgm";
import { Button } from "../../components/ui/Button";
import { Field, inputClass } from "../../components/ui/Field";
import type { ApiResult } from "../../lib/types";
import type { Provider } from "./providers";

interface Props {
  provider: Provider;
  onConnected: (result: cgmApi.CGMConnectResult) => void;
  onChangeProvider: () => void;
  /**
   * Override the submit endpoint. Defaults to `cgmApi.connect` (active
   * member). The Household screen passes the per-member wrapper so the
   * same form drives `/api/members/{member_id}/cgm/connect`.
   */
  submit?: (
    body: cgmApi.CGMConnectIn,
  ) => Promise<ApiResult<cgmApi.CGMConnectResult>>;
  /** Optional copy override above the form (e.g. "Connect Elena's Dexcom"). */
  title?: string;
  /** Cycle 88: "update" mode skips ProviderPicker (the caller renders the
   *  form directly with the known provider), prefills the username/region
   *  from current status, leaves the password blank, shows provider +
   *  region as a read-only summary, and focuses the password field. */
  mode?: "connect" | "update";
  /** Prefill for the username/email field (update mode). */
  initialIdentifier?: string;
  /** Prefill for the Dexcom region (update mode). */
  initialRegion?: "US" | "OUS";
  /** Shown as a secondary action in update mode (e.g. modal "Not now"). */
  onCancel?: () => void;
}

export function CGMConnectForm({
  provider,
  onConnected,
  onChangeProvider,
  submit,
  title,
  mode = "connect",
  initialIdentifier,
  initialRegion,
  onCancel,
}: Props) {
  const isUpdate = mode === "update";
  const [identifier, setIdentifier] = useState(initialIdentifier ?? "");
  const [pw, setPw] = useState("");
  const [region, setRegion] = useState<"US" | "OUS">(
    initialRegion ?? provider.defaultRegion,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier || !pw) return;
    setBusy(true);
    setError(null);
    const body: cgmApi.CGMConnectIn = {
      provider: provider.id,
      password: pw,
      ...(provider.id === "dexcom"
        ? { username: identifier, region }
        : { email: identifier }),
    };
    const r = await (submit ?? cgmApi.connect)(body);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    onConnected(r.data);
  };

  const idLabel = provider.id === "dexcom" ? "Dexcom username" : "LibreLinkUp email";

  return (
    <form
      onSubmit={onSubmit}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 420,
        background: "var(--bg-elev)",
        border: "0.5px solid var(--line-strong)",
        borderRadius: 16,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: `${provider.color}1A`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {provider.glyph(28)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.015em" }}>
            {title ?? (isUpdate ? "Update credentials" : `Connect ${provider.name}`)}
          </div>
          <div style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", marginTop: 2 }}>
            {isUpdate
              ? `${provider.name}${
                  provider.id === "dexcom"
                    ? ` · ${region === "US" ? "United States" : "Outside US"}`
                    : ""
                }`
              : provider.id === "dexcom"
                ? "Sugar Alarm signs into Dexcom Share with your account."
                : "Region is auto-detected on first connect."}
          </div>
        </div>
        {!isUpdate && (
          <button
            type="button"
            onClick={onChangeProvider}
            style={{
              appearance: "none",
              border: 0,
              background: "transparent",
              color: "var(--accent)",
              fontWeight: 600,
              fontSize: "var(--type-floor)",
              cursor: "pointer",
            }}
          >
            Change
          </button>
        )}
      </div>

      <Field label={idLabel}>
        <input
          type="text"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          autoFocus={!isUpdate}
          className={inputClass}
          placeholder={provider.id === "dexcom" ? "you" : "you@home.com"}
        />
      </Field>
      <Field label="Password">
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoFocus={isUpdate}
          className={inputClass}
          placeholder="••••••••••"
        />
      </Field>

      {/* Cycle 88: in update mode the region is part of the read-only
          summary above, so only username + password are typed (keeps the
          field count small for the kiosk on-screen keyboard). */}
      {provider.id === "dexcom" && !isUpdate && (
        <Field label="Region">
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value as "US" | "OUS")}
            className={inputClass}
          >
            <option value="US">United States</option>
            <option value="OUS">Outside US</option>
          </select>
        </Field>
      )}

      {error && (
        <div style={{ color: "var(--zone-urgent)", fontSize: "var(--type-floor)", fontWeight: 500 }}>
          {error}
        </div>
      )}

      <Button type="submit" full disabled={busy || !identifier || !pw}>
        {busy
          ? isUpdate
            ? "Updating…"
            : "Connecting…"
          : isUpdate
            ? "Update credentials"
            : `Connect ${provider.name}`}
      </Button>

      {isUpdate && onCancel && (
        <Button type="button" variant="ghost" full onClick={onCancel} disabled={busy}>
          Not now
        </Button>
      )}

      <div
        style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", textAlign: "center", marginTop: 4 }}
      >
        Credentials are encrypted at rest with Fernet (AES-128).
      </div>
    </form>
  );
}
